import React, { ReactElement } from 'react';

import './text-highlighter.scss';

type ChildType = (text: string) => ReactElement;

const defaultHighlight = (text: string) => (
  <span className="highlight" data-testid="highlight">
    {text}
  </span>
);

// Needed if the target includes ambiguous characters that are valid regex operators.
const escapeRegex = (v: string): string => v.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * Case-insensitive highlight which keeps the source casing.
 */
const highlightWord = (source: string, target?: string, callback?: ChildType) => {
  const res: ReactElement[] = [];

  if (!source) {
    return res;
  }

  if (!target) {
    return source;
  }

  let lastOffset = 0;

  const regex = new RegExp(escapeRegex(target), 'gi');

  // Uses replace callback, but not its return value
  source.replace(regex, (val: string, offset: number): string => {
    // Push both the last part of the string, and the new part with the highlight
    res.push(
      <span key={`text-${lastOffset}`}>{source.substring(lastOffset, offset - lastOffset)}</span>,
      // Replace the string with JSX or anything.
      React.cloneElement((callback || defaultHighlight)(val), { key: `highlight-${offset}` })
    );
    lastOffset = offset + val.length;

    return '';
  });

  // Push the last non-highlighted string
  res.push(<span key={`text-${lastOffset}`}>{source.substring(lastOffset)}</span>);

  return res;
};

type Props = {
  source: string;
  target?: string;
  children?: ChildType;
};

const TextHighlighter = ({ source, target, children }: Props) => <>{highlightWord(source, target, children)}</>;

export default TextHighlighter;
